import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Button, Card, Group, Segmented, Text, TextInput, Title } from '@grovus/design-system'
import { AuthLayout } from '../auth/AuthLayout'
import { LANG_OPTIONS, REQUEST_COPY, type Dict, type Lang } from '../i18n'
import type { Theme } from '../lib/preferences'
import { discardTicket, fetchTicket, fileAdmission, type OwnRequest } from './api'
import { Alert, formatDay, MessageField } from './parts'

/** Why the page hands back to the boundary: the sign-in screen, at `/` when admitted. */
export type RequestExit = 'noTicket' | 'admitted' | 'signedOut'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  onExit: (reason: RequestExit) => void
}

type View =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'form'; ticketId: string; email: string }
  | { kind: 'pending'; request: OwnRequest }
  | { kind: 'refused' }

type Notice = 'accountChanged' | 'submitFailed' | 'tooLong' | null

function viewOf(ticketId: string, email: string, request: OwnRequest | null): View {
  if (request?.status === 'pending') return { kind: 'pending', request }
  if (request?.status === 'rejected') return { kind: 'refused' }
  return { kind: 'form', ticketId, email }
}

/**
 * `/request-access`, screen 5's form, pending and refused boards for an identity the
 * callback held in a request ticket. The form remembers the ticket it was rendered for:
 * if another tab has since signed in a different account, the route refuses the submit,
 * and the page reloads for the current identity and says so before allowing another.
 */
export function RequestAccessScreen({ t, lang, setLang, theme, toggleTheme, onExit }: Props) {
  const copy = REQUEST_COPY[lang]
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [message, setMessage] = useState('')
  const [notice, setNotice] = useState<Notice>(null)
  const [busy, setBusy] = useState(false)
  const [signOut, setSignOut] = useState<'idle' | 'pending' | 'failed'>('idle')
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const exit = useRef(onExit)
  useEffect(() => { exit.current = onExit }, [onExit])

  const load = useCallback(async (): Promise<boolean> => {
    const result = await fetchTicket()
    if (!mounted.current || result.status === 'stale') return false
    if (result.status === 'unauthorized') {
      exit.current('noTicket')
      return false
    }
    if (result.status !== 'ok') {
      setView({ kind: 'failed' })
      return false
    }
    const ticket = result.body
    // An approved admission request admits its subject, so the store answers `admitted`;
    // the approved row is read the same way should the two ever disagree.
    if (ticket.kind === 'admitted' || ticket.request?.status === 'approved') {
      exit.current('admitted')
      return false
    }
    setView(viewOf(ticket.ticketId, ticket.email, ticket.request))
    return true
  }, [])

  useEffect(() => { void load() }, [load])

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (view.kind !== 'form' || busy) return
    setBusy(true)
    setNotice(null)
    const result = await fileAdmission(view.ticketId, message)
    if (!mounted.current || result.status === 'stale') return
    if (result.status === 'changed') {
      // Another account holds the ticket now. Its form must not carry this message, and
      // nothing can be sent until the page shows whom it would be sent for.
      setMessage('')
      setNotice('accountChanged')
      await load()
      if (mounted.current) setBusy(false)
      return
    }
    setBusy(false)
    if (result.status === 'ok') {
      setView({ kind: 'pending', request: result.body })
    } else if (result.status === 'unauthorized') {
      onExit('noTicket')
    } else if (result.status === 'refused') {
      if (result.code === 'already_admitted') onExit('admitted')
      else if (result.code === 'already_pending' && result.request) setView({ kind: 'pending', request: result.request })
      else if (result.code === 'already_refused') setView({ kind: 'refused' })
      else setNotice('submitFailed')
    } else {
      setNotice(result.status === 'tooLong' ? 'tooLong' : 'submitFailed')
    }
  }

  async function onSignOut() {
    setSignOut('pending')
    const result = await discardTicket()
    if (!mounted.current || result.status === 'stale') return
    // Only the route's 204 ends the ticket. Anything else leaves it working, so the page
    // stays with a retry rather than looking signed out (F1).
    if (result.status === 'ok') {
      onExit('signedOut')
      return
    }
    setSignOut('failed')
  }

  const signOutButton = (
    <Button variant="subtle" loading={signOut === 'pending'} onClick={onSignOut}>
      {signOut === 'failed' ? copy.retry : copy.signOut}
    </Button>
  )

  return (
    <AuthLayout
      topRight={(
        <>
          <Segmented
            value={lang}
            onChange={(value) => setLang(value as Lang)}
            data={LANG_OPTIONS}
          />
          <Button variant="default" onClick={toggleTheme}>
            {theme === 'light' ? t.dark : t.light}
          </Button>
        </>
      )}
    >
      <Card>
        <Title order={1} fz={18}>{copy.title}</Title>

        {view.kind === 'loading' && <Text fz="sm" mt={6} role="status">{copy.loading}</Text>}

        {view.kind === 'failed' && (
          <>
            <Alert>{copy.loadFailed}</Alert>
            <Button mt="md" onClick={() => { setView({ kind: 'loading' }); void load() }}>{copy.retry}</Button>
          </>
        )}

        {view.kind === 'form' && (
          <form onSubmit={onSubmit}>
            <Text fz="sm" mt={6} className="grv-muted">{copy.lede}</Text>
            {notice === 'accountChanged' && (
              <Text fz="sm" mt="sm" className="grv-muted" role="status">{copy.accountChanged}</Text>
            )}
            <TextInput mt="md" label={copy.email} value={view.email} readOnly />
            <MessageField
              id="request-access-message" label={copy.use} copy={copy}
              value={message} onChange={setMessage} disabled={busy}
            />
            {(notice === 'submitFailed' || notice === 'tooLong') && <Alert>{copy[notice]}</Alert>}
            <Group mt="md" gap="sm">
              <Button type="submit" loading={busy} disabled={busy}>{copy.submit}</Button>
              {signOutButton}
            </Group>
          </form>
        )}

        {view.kind === 'pending' && (
          <>
            <Text fz="sm" mt={6}>{copy.sent(formatDay(view.request.requestedAt, copy))}</Text>
            <Text fz="sm" mt={4} className="grv-muted">{copy.sentLede}</Text>
            <Group mt="md">{signOutButton}</Group>
          </>
        )}

        {view.kind === 'refused' && (
          <>
            <Text fz="sm" mt={6}>{copy.refused}</Text>
            <Text fz="sm" mt={4} className="grv-muted">{copy.refusedLede}</Text>
            <Group mt="md">{signOutButton}</Group>
          </>
        )}

        {signOut === 'failed' && <Alert>{copy.signOutFailed}</Alert>}
      </Card>
    </AuthLayout>
  )
}
